import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { BarCode } from '@/components-latest/primitives/Icons'
import Separator from '@/components-latest/primitives/Separator'
import Text from '@/components-latest/primitives/Text'
import { SupportedLanguages } from '@/config/i18n'
import { IAddon, IFood, IPack } from '@/ducks/orders/orders.types'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import { formatPrice } from '@/utils/currency.utils'

import Expandable from './Expandable'
import Styles from './PaymentDetalization.module.scss'

const Pack: React.FC<{ pack?: IPack }> = ({ pack }) => {
  const { i18n, t } = useTranslation()
  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

  const currency = useSelector(selectCurrencyCode) || ''

  if (!pack) return null
  const { sub_pack } = pack
  if (!sub_pack || sub_pack.length === 0) return null

  return (
    <div className={Styles.sub}>
      <Text align="center" variant="nextCaption" className={Styles.subTitle}>
        {pack.t_name[language] || t('orders.base-pack')}
      </Text>
      {sub_pack.map(({ name, price, _id }) => (
        <div className={Styles.keyValue} key={_id}>
          <Text variant="nextBody">{name[language]}</Text>
          <Text variant="nextBody">
            {formatPrice(price || 0, currency, {
              signDisplay: 'never'
            })}
          </Text>
        </div>
      ))}
    </div>
  )
}

const Addons: React.FC<{ name: string; addons?: IAddon[] }> = ({
  name,
  addons
}) => {
  const { i18n } = useTranslation()
  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

  const currency = useSelector(selectCurrencyCode) || ''

  if (!addons || addons.length === 0) return null

  return (
    <div className={Styles.sub}>
      <Text align="center" variant="nextCaption" className={Styles.subTitle}>
        {name}
      </Text>
      {addons.map(({ name, price }) => (
        <div className={Styles.keyValue} key={name[language]}>
          <Text variant="nextBody">{name[language]}</Text>
          <Text variant="nextBody">
            {formatPrice(price || 0, currency, {
              signDisplay: 'never'
            })}
          </Text>
        </div>
      ))}
    </div>
  )
}

interface IPaymentDetalizationProps {
  products: IFood[]
  variant?: 'darkStore' | 'default'
  className?: string
}

const PaymentDetalization: React.FC<IPaymentDetalizationProps> = ({
  products,
  variant = 'default',
  className
}) => {
  const { t, i18n } = useTranslation()
  const language = (i18n.resolvedLanguage || 'en') as SupportedLanguages

  const currency = useSelector(selectCurrencyCode) || ''
  const isDarkStore = variant === 'darkStore'

  const hasSize = (item: IFood['type_pack']) =>
    item?.[0]?.t_name?.[language] && item?.[0]?.sub_pack?.[0]?.name?.[language]

  const totalItems = products.reduce((sum, { quantity }) => {
    return sum + (quantity || 1)
  }, 0)

  return (
    <div
      className={clsx(Styles.paymentDetalization, className)}
      data-testid="payment-detalization">
      <header className={Styles.keyValue}>
        <Text variant="body" weight="bold">
          {t('orders.total-number')}
        </Text>
        <Text variant="body" weight="bold">
          {t('orders.count-items', { count: totalItems })}
        </Text>
      </header>

      <Separator />

      {products.map(
        (
          {
            barcode,
            main_category,
            id,
            quantity,
            name,
            base_pack,
            type_pack,
            addons,
            total,
            instruction
          },
          index
        ) => (
          <div className={Styles.product} key={id}>
            <div className={Styles.main}>
              <Text
                variant="nextBody"
                weight="semiBold"
                className={Styles.category}>
                {main_category?.[language]}
              </Text>
              <Text variant="nextSubtitle" weight="bold">
                {quantity} × {name[language]}
              </Text>
              {isDarkStore && barcode && (
                <div className={Styles.barCode}>
                  <BarCode width={16} height={16} />
                  <Text variant="serial">{barcode}</Text>
                </div>
              )}
              {!isDarkStore && hasSize(type_pack) && (
                <Text variant="nextBody">
                  {type_pack[0].t_name[language]}:{' '}
                  {type_pack[0].sub_pack[0].name[language]}
                </Text>
              )}
            </div>

            {!isDarkStore && (
              <Addons name={t('orders.addons')} addons={addons} />
            )}
            {!isDarkStore &&
              base_pack.map((basePack, index) => (
                <Pack
                  key={basePack?.sub_pack?.[0]?._id || index}
                  pack={basePack}
                />
              ))}

            <Separator variant="squared" />

            <div className={Styles.keyValue}>
              <Text variant="nextBody" weight="bold">
                {t('orders.total')}
              </Text>
              <Text variant="nextBody" weight="bold">
                {formatPrice(total || 0, currency, {
                  signDisplay: 'never'
                })}
              </Text>
            </div>

            {instruction && !isDarkStore && (
              <Expandable>{instruction}</Expandable>
            )}

            {index !== products.length - 1 && <Separator />}

            {!isDarkStore && index === 0 && products.length > 1 && (
              <Separator variant="squared" className={Styles.dimmed} />
            )}
          </div>
        )
      )}
    </div>
  )
}

export default PaymentDetalization
